import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAppContext } from './Core';
import { useCampaignContext } from './Campaign';
import { getAttributeFilterValues, getFilteredRecipientsCount, updateFilterAPI, saveFilterAPI, deleteFilterAPI, downloadRecipientsAPI } from '../APIs/RecipientFilterAPI';
import { RECIPIENT_FILTER_ATTRIBUTES } from '../consts/recipientFilter';
import { SNACK_TYPES } from '../consts/Common';
import { JWT } from '../consts/AuthFields';
import { TEAM } from '../consts/DBFields';
import { useSavedFiltersManagement } from './SavedFiltersManagement';

// Create and export the consumer, which allows state to be used by other components
export const Context = createContext();
export const RecipientFilterState = Context.Consumer;
export const useRecipientFilter = () => useContext(Context);

const RecipientFilter = ({children}) => {
    // Get dependencies from context
    const {
        setSnackMessage,
        setSnackType,
        user,
        selectedTeam,
    } = useAppContext();

    const {
        selectedCampaign,
    } = useCampaignContext();

    // Initialize required state
    const [filterName, setFilterName] = useState("");
    const [filterId, setFilterId] = useState(null);
    //Filter string
    //Eg: attr1:[val1]&attr2:[val2]&attr3:[val3]|attr4:[val4]|attr5:[val5,val6]
    //"EntityParent:[Option 1,Option 2,Option 3]&StoreAscInd:[Option 1,Option 3,Option 6,Option 5,Option 4]|JobFamily:[Option 1,Option 2]"
    const [rawFilter, setRawFilter] = useState("");
    //Filter string parsed into chunks of conditions and operators
    /* Eg: 
        [
            {type: 'condition', attribute: 'attr1', values: ['val1']},
            {type: 'operator', value: 'AND'},
            {type: 'condition', attribute: 'attr2', values: ['val2']},
            {type: 'operator', value: 'AND'},
            {type: 'condition', attribute: 'attr3', values: ['val3']},
            {type: 'operator', value: 'OR'},
            {type: 'condition', attribute: 'attr4', values: ['val4']},
            {type: 'operator', value: 'OR'},
            {type: 'condition', attribute: 'attr5', values: ['val5','val6']}
        ]
    */
    const [parsedFilter, setParsedFilter] = useState([]);
    //Recipients count for the selected filter
    const [recipientCount, setRecipientCount] = useState(0);
    const [sharedWith, setSharedWith] = useState([]); // Users with whom the filter is shared
    const [alertMessage, setAlertMessage] = useState({
        title: '',
        message: '',
        cancelButtonText: '',
        confirmButtonText: '',
        handleConfirm: () => {}
    });

    // Compute available attributes by filtering out those already used
    const recipientFilterAttributesOptions = useMemo(() => {
        const usedAttributes = new Set(
            parsedFilter
                .filter(item => item.type === 'condition' && item.attribute)
                .map(item => item.attribute)
        );
        
        return RECIPIENT_FILTER_ATTRIBUTES.filter(attr => !usedAttributes.has(attr.value));
    }, [parsedFilter]);

    useEffect(() => {
        setParsedFilter(parseFilter(rawFilter));
        // generateRawFilter(parsedFilter);
    }, [rawFilter]);

    useEffect(() => {
        if(!parsedFilter || parsedFilter.length <= 0) return;
        const newRawFilter = generateRawFilter(parsedFilter);
        fetchRecipientCount(newRawFilter);
        
        // setRawFilter(newRawFilter);
    }, [parsedFilter]);

    const fetchRecipientCount = async (filter) => {
        try {
            const params = {
                filter: filter,
            }
            const response = await getFilteredRecipientsCount(params);
            setRecipientCount(response.count);
            return response;
        } catch (error) {
            setSnackMessage("Error fetching recipient count");
            setSnackType("error");
            setRecipientCount(0);
        }
    };

    const parseFilter = (filter) => {
        if (!filter) return [];
        
        const parsed = [];
        
        // Use regex to find all condition patterns and operators between them
        // This regex matches: attribute:[values] followed by optional operator (&|)
        const conditionRegex = /([^:\[&|]+):\[([^\]]*)\](?:([&|]))?/g;
        
        let match;
        while ((match = conditionRegex.exec(filter)) !== null) {
            const [, attribute, valuesStr, operator] = match;
            
            // Add the condition
            const values = valuesStr ? 
                valuesStr.split(',').map(v => v.trim()).filter(v => v) : [];
            
            parsed.push({
                type: 'condition',
                attribute: attribute.trim(),
                values: values
            });
            
            // Add the operator if it exists
            if (operator) {
                parsed.push({
                    type: 'operator',
                    value: operator === '&' ? 'AND' : 'OR'
                });
            }
        }
        
        return parsed;
    }

    const generateRawFilter = (parsedFilter) => {
        const parts = [];
        parsedFilter.forEach(item => {
            if (item.type === 'condition') {
                parts.push(`${item.attribute}:[${item.values.join(',')}]`);
            } else if (item.type === 'operator') {
                parts.push(item.value === 'AND' ? '&' : '|');
            }
        });
        return parts.join('');
    }

    const getIndexOfLastCondition = () => {
        for (let i = parsedFilter.length - 1; i >= 0; i--) {
            if (parsedFilter[i].type === 'condition') {
                return i;
            }
        }
        return -1; // No condition found
    }

    const getIndexOfLastOperator = () => {
        for (let i = parsedFilter.length - 1; i >= 0; i--) {
            if (parsedFilter[i].type === 'operator') {
                return i;
            }
        }
        return -1; // No operator found
    }

    const updateOperator = (index, newOperator) => {
        const _parsedFilter = [...parsedFilter];
        if(index >= _parsedFilter.length || _parsedFilter[index].type !== 'operator') return;
        if (_parsedFilter[index]) {
            _parsedFilter[index].value = newOperator;
        }
        setParsedFilter(_parsedFilter);
    }

    const updateCriteria = (index, attribute, value) => {
        const _parsedFilter = [...parsedFilter];
        if(_parsedFilter.length === 0) {
            _parsedFilter.push({
                type: 'condition',
                attribute: attribute,
                values: value
            });
            setParsedFilter(_parsedFilter);
            return;
        }
        if(index >= _parsedFilter.length || _parsedFilter[index].type !== 'condition') return;
        if (_parsedFilter[index]) {
            _parsedFilter[index].attribute = attribute;
            _parsedFilter[index].values = value;
        }
        setParsedFilter(_parsedFilter);
    }

    const handleAddFilter = () => {
        if(!isValid) {
            setAlertMessage({
                show: true,
                title: "Invalid Filter",
                message: "Please fill all the filter conditions before adding a new one.",
                cancelButtonText: "Close",
                confirmButtonText: "Okay",
                handleConfirm: () => setAlertMessage({ ...alertMessage, show: false })
            });
            return;
        }
        const _parsedFilter = [...parsedFilter];
        _parsedFilter.push({
            type: 'operator',
            value: null,
        });
        _parsedFilter.push({
            type: 'condition',
            attribute: null,
            values: []
        });
        setParsedFilter(_parsedFilter);
    }

    const generateRawFilterFromRange = (startIndex, endIndex) => {
        const parts = [];
        for (let i = startIndex; i <= endIndex; i++) {
            const item = parsedFilter[i];
            if (item.type === 'condition') {
                parts.push(`${item.attribute}:[${item.values.join(',')}]`);
            } else if (item.type === 'operator') {
                parts.push(item.value === 'AND' ? '&' : '|');
            }
        }
        return parts.join('');
    }

    const generateRawFilterForGroup = (index) => {
        if(index < 0 || index >= parsedFilter.length) return "";
        const previousOrOperatorIndex = findIndexOfPreviousOrOperator(index);
        const startIndex = previousOrOperatorIndex === 0 ? 0 : previousOrOperatorIndex + 1;
        const endIndex = index - 2;
        return generateRawFilterFromRange(startIndex, endIndex);
    }

    const getAllTheAttributesUsedInFilterRange = (startIndex, endIndex) => {
        const attributes = new Set();
        for (let i = startIndex; i <= endIndex; i++) {
            if (parsedFilter[i] && parsedFilter[i].type === 'condition' && parsedFilter[i].attribute) {
                attributes.add(parsedFilter[i].attribute);
            }
        }
        return Array.from(attributes);
    }

    const getAllTheAttributesUsedInGroup = (index) => {
        const previousOrOperatorIndex = findIndexOfPreviousOrOperator(index);
        const startIndex = previousOrOperatorIndex === 0 ? 0 : previousOrOperatorIndex + 1;
        const endIndex = index - 2;
        if(startIndex > endIndex) return [];
        return getAllTheAttributesUsedInFilterRange(startIndex, endIndex);
    }

    const findIndexOfPreviousOrOperator = (startIndex) => {
        let i = startIndex - 1;
        for (; i >= 0; i--) {
            if (parsedFilter[i].type === 'operator' && parsedFilter[i].value === 'OR') {
                return i;
            }
        }
        return i;
    }

    const fetchAttributeFilterValues = async (filter, field) => {
        try {
            const response = await getAttributeFilterValues({ filter, field });
            return {
                values: response?.attributeValues?.values || [],
                count: response?.attributeValues?.count || 0
            };
        } catch (error) {
            console.error('Error fetching attribute filter values:', error);
            return null;
        }
    };

    const validate = () => {
        return validateUpto(parsedFilter.length - 1);
    }

    const validateUpto = (index) => {
        const errors = [];
        for (let i = 0; i <= index && i < parsedFilter.length; i++) {
            const item = parsedFilter[i];
            if (item.type === 'condition') {
                if (!item.attribute) {
                    errors.push(`Condition ${i + 1} is missing an attribute`);
                }
                if (item.values.length === 0) {
                    errors.push(`Condition ${i + 1} is missing values`);
                }
            } else if (item.type === 'operator') {
                if (!item.value) {
                    errors.push(`Operator ${i + 1} is missing a value`);
                }
            }
        }
        return errors;
    }

    const saveNewFilter = async () => {
        const _filterName = filterName.trim();
        if(_filterName.length < 3) {
            setSnackMessage("Recipient list name must be at least 3 characters");
            setSnackType(SNACK_TYPES.ERROR);
            return;
        }
        const errors = validate();
        const isDraft = false;
        if(errors.length > 0) {
            isDraft = true;
            return;
        }
        const filterObj = {
            name: _filterName,
            filter: generateRawFilter(parsedFilter),
            recipient_count: recipientCount,
            team_id: selectedTeam[TEAM.ID],
            shared_with: sharedWith,
        }
        try {
            const result = await saveFilterAPI(filterObj);
            const parts = result.split(':');
            if (parts.length >= 2) {
                setFilterId(parts[parts.length - 1].trim());
            }
            setSnackMessage("Recipient filters saved.");
            setSnackType(SNACK_TYPES.SUCCESS);
        } catch (err) {
            setSnackMessage("Failed to save recipient filter");
            setSnackType(SNACK_TYPES.ERROR);
            return err;
        }

    }

    const updateFilter = async () => {
        const _filterName = (filterName || '').trim();
        if (_filterName.length < 3) {
            setSnackMessage("Recipient list name must be at least 3 characters");
            setSnackType(SNACK_TYPES.ERROR);
            return "validation_error";
        }
        const errors = validate();
        const isDraft = false;
        if(errors.length > 0) {
            isDraft = true;
            return "validation_error";
        }
        const filterObj = {
            name: _filterName,
            filter: generateRawFilter(parsedFilter),
            recipient_count: recipientCount,
            team_id: selectedTeam[TEAM.ID],
            shared_with: sharedWith,
        };
        try {
            const result = await updateFilterAPI(filterId, filterObj);
            setSnackMessage("Recipient Filters updated.");
            setSnackType(SNACK_TYPES.SUCCESS);
        } catch(err) {
            setSnackMessage("Failed to update recipient filter");
            setSnackType(SNACK_TYPES.ERROR);
            return err;
        }
    }

    const deleteFilter = async (filterId) => {
        try{
          const response = await deleteFilterAPI(filterId);
          setSnackType(SNACK_TYPES.SUCCESS);
          setSnackMessage("Recipient Filter deleted.");
          return response;
        } catch (error) {
          setSnackType(SNACK_TYPES.ERROR);
          setSnackMessage("Error occured while deleting Recipient filter");
        }
    };


    const isValid = useMemo(() => {
    const errors = validate();
        return errors.length === 0;
    }, [parsedFilter]);

    const isValidUpto = (index) => {
        if(index <= 0) return true;
        const errors = validateUpto(index);
        return errors.length === 0;
    }

    const deleteCondition = (index) => {
        if(index < 1 || index >= parsedFilter.length) return;
        //it should remove the values from parsedFilter from the index to parsedFilter.length
        const _parsedFilter = [...parsedFilter];
        _parsedFilter.splice(index - 1);
        setParsedFilter(_parsedFilter);
    }

    const resetFilter = () => {
        setFilterName("");
        setFilterId(null);
        setRawFilter("");
        setParsedFilter([]);
        setRecipientCount(0);
        setSharedWith([]);
        setAlertMessage({
            title: '',
            message: '',
            cancelButtonText: '',
            confirmButtonText: '',
            handleConfirm: () => {}
        });
    }

    const initialState = {
        rawFilter,
        setRawFilter,
        setParsedFilter,
        generateRawFilter,
        recipientCount,
        fetchRecipientCount,
        parsedFilter,
        updateOperator,
        updateCriteria,
        handleAddFilter,
        recipientFilterAttributesOptions,
        validate,
        isValid,
        alertMessage,
        setAlertMessage,
        getIndexOfLastCondition,
        getIndexOfLastOperator,
        fetchAttributeFilterValues,
        saveNewFilter,
        updateFilter,
        filterName,
        setFilterName,
        filterId,
        setFilterId,
        deleteCondition,
        generateRawFilterForGroup,
        getAllTheAttributesUsedInGroup,
        isValidUpto,
        parseFilter,
        validate,
        resetFilter,
        deleteFilter,
        sharedWith,
        setSharedWith,
    };

    return <Context.Provider value={initialState}>{children}</Context.Provider>;
}

export default RecipientFilter;